import React, { useState, useRef, useCallback } from 'react';
import {
	Alert,
	AlertTitle,
	Box,
	Button,
	Chip,
	CircularProgress,
	Paper,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Tooltip,
	Typography,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import DownloadIcon from '@mui/icons-material/Download';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SendIcon from '@mui/icons-material/Send';
import { ProviderPayableLinePaymentTypeEnum } from '../../types';
import { getProviderPayableLinesPending, bulkCreateProviderPayableLinePayments } from '../../services';
import { extractErrorMessage } from '../../../quotation/helpers';

const MAX_ROWS = 1000;

const CSV_COLUMNS = [
	'providerPayableLineId',
	'paymentDate',
	'amount',
	'paymentType',
	'notes',
	'observation',
	'localCurrency',
	'exchangeRate',
];

const VALID_PAYMENT_TYPES = Object.values(ProviderPayableLinePaymentTypeEnum);

interface ParsedRow {
	rowIndex: number;
	providerPayableLineId: string;
	paymentDate: string;
	amount: number;
	paymentType: string;
	notes: string;
	observation: string;
	localCurrency?: string;
	exchangeRate?: number;
}

interface RowImportResult {
	status: 'Created' | 'Failed';
	paymentId?: string;
	error?: string;
}

interface ValidatedGroup {
	providerPayableLineId: string;
	pending: number | undefined;
	totalAmount: number;
	rows: ParsedRow[];
	groupErrors: string[];
	rowErrors: Record<number, string[]>;
	rowImportResults: Record<number, RowImportResult>;
	isValid: boolean;
}

type BulkStep = 'upload' | 'validating' | 'preview' | 'submitting' | 'done';

const parseCSVLine = (line: string): string[] => {
	const result: string[] = [];
	let current = '';
	let inQuotes = false;

	for (let i = 0; i < line.length; i++) {
		const char = line[i];
		if (char === '"') {
			inQuotes = !inQuotes;
		} else if (char === ',' && !inQuotes) {
			result.push(current.trim());
			current = '';
		} else {
			current += char;
		}
	}

	result.push(current.trim());
	return result;
};

const parseNumeric = (s: string): number => parseFloat(s.replace(',', '.'));

const parseCSV = (text: string): { rows: ParsedRow[]; errors: string[] } => {
	const lines = text
		.trim()
		.split(/\r?\n/)
		.map((l) => l.trim())
		.filter(Boolean);

	const errors: string[] = [];
	const rows: ParsedRow[] = [];

	if (lines.length < 2) {
		return { rows: [], errors: ['El archivo no contiene datos (está vacío o solo tiene encabezado)'] };
	}

	for (let i = 1; i < lines.length; i++) {
		const rowNum = i + 1;
		const cols = parseCSVLine(lines[i]);

		if (cols.length < 4) {
			errors.push(`Fila ${rowNum}: columnas insuficientes (mínimo 4)`);
			continue;
		}

		const [
			rawId = '',
			rawDate = '',
			rawAmount = '',
			rawType = '',
			rawNotes = '',
			rawObs = '',
			rawCurrency = '',
			rawRate = '',
		] = cols;

		const normalizedCurrency = rawCurrency.trim();
		const normalizedRate = rawRate.trim();

		rows.push({
			rowIndex: rowNum,
			providerPayableLineId: rawId.trim(),
			paymentDate: rawDate.trim(),
			amount: parseNumeric(rawAmount.trim()),
			paymentType: rawType.trim(),
			notes: rawNotes.trim(),
			observation: rawObs.trim(),
			...(normalizedRate
				? {
						exchangeRate: parseNumeric(normalizedRate),
						...(normalizedCurrency ? { localCurrency: normalizedCurrency } : {}),
				  }
				: {}),
		});
	}

	return { rows, errors };
};

const validateGroups = (rows: ParsedRow[], pendingMap: Map<string, number>): ValidatedGroup[] => {
	const groupMap = new Map<string, ParsedRow[]>();

	rows.forEach((row) => {
		const existing = groupMap.get(row.providerPayableLineId) ?? [];
		groupMap.set(row.providerPayableLineId, [...existing, row]);
	});

	return Array.from(groupMap.entries()).map(([id, groupRows]) => {
		const pending = pendingMap.get(id);
		const totalAmount = groupRows.reduce((sum, r) => sum + (isNaN(r.amount) ? 0 : r.amount), 0);
		const groupErrors: string[] = [];
		const rowErrors: Record<number, string[]> = {};

		if (pending === undefined) {
			groupErrors.push('ID de línea no encontrado en el sistema');
		} else if (totalAmount > pending) {
			groupErrors.push(`Suma de pagos USD ${totalAmount.toFixed(2)} supera el pendiente USD ${pending.toFixed(2)}`);
		}

		groupRows.forEach((row) => {
			const errs: string[] = [];

			if (!row.providerPayableLineId) errs.push('ID de línea requerido');
			if (!row.paymentDate) errs.push('Fecha requerida');
			if (isNaN(row.amount)) errs.push('Monto inválido');

			if (!VALID_PAYMENT_TYPES.includes(row.paymentType as ProviderPayableLinePaymentTypeEnum)) {
				errs.push(
					`Tipo "${row.paymentType}" inválido. Los posibles valores permitidos son: ${VALID_PAYMENT_TYPES.join(', ')}`,
				);
			}

			if (row.exchangeRate !== undefined && (isNaN(row.exchangeRate) || row.exchangeRate <= 0)) {
				errs.push('exchangeRate inválido');
			}

			if (errs.length > 0) {
				rowErrors[row.rowIndex] = errs;
			}
		});

		return {
			providerPayableLineId: id,
			pending,
			totalAmount,
			rows: groupRows,
			groupErrors,
			rowErrors,
			rowImportResults: {},
			isValid: groupErrors.length === 0 && Object.keys(rowErrors).length === 0,
		};
	});
};

const downloadTemplate = (): void => {
	const header = CSV_COLUMNS.join(',');
	const rows = [
		'ppl-001,2026-03-15,100.00,C2A,BSP W03M05,Auth 013383 XXXX,USD,1',
		'ppl-001,2026-03-16,50.00,Cash,Transferencia,TRF ITAU 058789,UYU,43.5',
		'ppl-002,2026-03-15,446.00,BankTransfer,Pago total,,USD,1',
	];

	const blob = new Blob([[header, ...rows].join('\n')], { type: 'text/csv;charset=utf-8;' });
	const url = URL.createObjectURL(blob);
	const a = document.createElement('a');
	a.href = url;
	a.download = 'plantilla_pagos_masivos.csv';
	a.click();
	URL.revokeObjectURL(url);
};

const fmtUSD = (n: number): string =>
	`USD ${n.toLocaleString('es-UY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const ProviderPaymentsBulk = (): JSX.Element => {
	const [step, setStep] = useState<BulkStep>('upload');
	const [fileName, setFileName] = useState('');
	const [parseError, setParseError] = useState('');
	const [isDragging, setIsDragging] = useState(false);
	const [groups, setGroups] = useState<ValidatedGroup[]>([]);
	const [submitResult, setSubmitResult] = useState<{ success: number; failed: number; error: string | null } | null>(
		null,
	);
	const fileInputRef = useRef<HTMLInputElement>(null);

	const processFile = useCallback(async (file: File) => {
		setParseError('');

		if (!file.name.toLowerCase().endsWith('.csv')) {
			setParseError('El archivo debe ser CSV (.csv)');
			return;
		}

		const text = await file.text();
		const { rows, errors } = parseCSV(text);

		if (errors.length > 0) {
			setParseError(errors.join('\n'));
			return;
		}

		if (rows.length === 0) {
			setParseError('El archivo no contiene filas de datos');
			return;
		}

		if (rows.length > MAX_ROWS) {
			setParseError(`El archivo tiene ${rows.length} filas. El máximo permitido es ${MAX_ROWS}`);
			return;
		}

		setFileName(file.name);
		setStep('validating');

		try {
			const uniqueIds = rows
				.map((r) => r.providerPayableLineId)
				.filter((value, index, array) => array.indexOf(value) === index);

			const response = await getProviderPayableLinesPending(uniqueIds);
			const pendingMap = new Map<string, number>(response.data.map((item) => [item.id, item.pending]));
			setGroups(validateGroups(rows, pendingMap));
			setStep('preview');
		} catch (error) {
			setParseError(extractErrorMessage(error, 'Error al consultar los pendientes. Intente nuevamente.'));
			setStep('upload');
		}
	}, []);

	const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (file) processFile(file);
		e.target.value = '';
	};

	const handleDrop = (e: React.DragEvent) => {
		e.preventDefault();
		setIsDragging(false);
		const file = e.dataTransfer.files?.[0];
		if (file) processFile(file);
	};

	const handleSubmit = async () => {
		setStep('submitting');

		const validGroups = groups.filter((g) => g.isValid);

		const payload = validGroups.map((g) => ({
			payableLineId: g.providerPayableLineId,
			payments: g.rows.map((r) => ({
				paymentDate: r.paymentDate,
				amount: r.amount,
				paymentType: r.paymentType as ProviderPayableLinePaymentTypeEnum,
				...(r.notes && { notes: r.notes }),
				...(r.observation && { observation: r.observation }),
				...(r.exchangeRate !== undefined
					? {
							exchangeRate: r.exchangeRate,
							...(r.localCurrency ? { localCurrency: r.localCurrency } : {}),
					  }
					: {}),
			})),
		}));

		try {
			const response = await bulkCreateProviderPayableLinePayments(payload);

			const responseMap = new Map(response.data.map((item) => [item.payableLineId, item.results]));

			let successCount = 0;
			let failedCount = 0;

			const updatedGroups = groups.map((group) => {
				if (!group.isValid) return group;

				const results = responseMap.get(group.providerPayableLineId) ?? [];
				const rowImportResults: Record<number, RowImportResult> = {};

				group.rows.forEach((row, index) => {
					const result = results[index];

					if (result?.status === 'Created') {
						rowImportResults[row.rowIndex] = {
							status: 'Created',
							paymentId: result.paymentId,
						};
						successCount += 1;
						return;
					}

					if (result?.status === 'Failed') {
						rowImportResults[row.rowIndex] = {
							status: 'Failed',
							error: result.error || 'Error al crear el pago',
						};
						failedCount += 1;
						return;
					}

					rowImportResults[row.rowIndex] = {
						status: 'Failed',
						error: 'No se recibió resultado para esta fila',
					};
					failedCount += 1;
				});

				return {
					...group,
					rowImportResults,
				};
			});

			setGroups(updatedGroups);
			setSubmitResult({
				success: successCount,
				failed: failedCount,
				error: null,
			});
		} catch (error) {
			console.log(error);
			setSubmitResult({
				success: 0,
				failed: 0,
				error: extractErrorMessage(error, 'Error al procesar el request. Intente nuevamente.'),
			});
		}

		setStep('done');
	};

	const handleReset = () => {
		setStep('upload');
		setFileName('');
		setParseError('');
		setGroups([]);
		setSubmitResult(null);
	};

	const totalRows = groups.reduce((s, g) => s + g.rows.length, 0);
	const validGroups = groups.filter((g) => g.isValid);
	const invalidGroups = groups.filter((g) => !g.isValid);

	if (step === 'validating') {
		return (
			<Box sx={{ textAlign: 'center', py: 10 }}>
				<CircularProgress size={48} />
				<Typography variant="h6" sx={{ mt: 2 }}>
					Validando líneas de pago...
				</Typography>
				<Typography color="text.secondary">Consultando pendientes en el sistema</Typography>
			</Box>
		);
	}

	if (step === 'submitting') {
		return (
			<Box sx={{ textAlign: 'center', py: 10 }}>
				<CircularProgress size={48} />
				<Typography variant="h6" sx={{ mt: 2 }}>
					Importando pagos...
				</Typography>
				<Typography color="text.secondary">Procesando {validGroups.length} líneas</Typography>
			</Box>
		);
	}

	if (step === 'upload') {
		return (
			<Box>
				<Alert severity="info" sx={{ mb: 3 }}>
					<AlertTitle>Formato del CSV</AlertTitle>
					<Typography variant="body2" sx={{ fontFamily: 'monospace', mt: 0.5, wordBreak: 'break-all' }}>
						{CSV_COLUMNS.join(', ')}
					</Typography>
					<Typography variant="body2" sx={{ mt: 1 }}>
						Tipos de pago válidos: <strong>{VALID_PAYMENT_TYPES.join(', ')}</strong>
					</Typography>
					<Typography variant="body2">
						Máximo <strong>{MAX_ROWS.toLocaleString()} filas</strong> · Fechas <strong>YYYY-MM-DD</strong> · Montos
						siempre en USD
					</Typography>
					<Button
						size="small"
						startIcon={<DownloadIcon />}
						onClick={downloadTemplate}
						variant="outlined"
						sx={{ mt: 1.5 }}
					>
						Descargar plantilla
					</Button>
				</Alert>

				<Paper
					variant="outlined"
					onClick={() => fileInputRef.current?.click()}
					onDrop={handleDrop}
					onDragOver={(e) => {
						e.preventDefault();
						setIsDragging(true);
					}}
					onDragLeave={() => setIsDragging(false)}
					sx={{
						border: '2px dashed',
						borderColor: isDragging ? 'primary.main' : 'divider',
						bgcolor: isDragging ? 'action.hover' : 'background.paper',
						borderRadius: 2,
						p: 8,
						textAlign: 'center',
						cursor: 'pointer',
						transition: 'border-color 0.2s, background-color 0.2s',
						'&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
					}}
				>
					<UploadFileIcon sx={{ fontSize: 52, color: 'text.secondary', mb: 1 }} />
					<Typography variant="h6" color="text.secondary">
						Arrastrá o hacé click para subir el CSV
					</Typography>
					<Typography variant="body2" color="text.disabled" sx={{ mt: 0.5 }}>
						Máximo {MAX_ROWS.toLocaleString()} filas
					</Typography>
					<input ref={fileInputRef} type="file" accept=".csv" style={{ display: 'none' }} onChange={handleFileChange} />
				</Paper>

				{parseError && (
					<Alert severity="error" sx={{ mt: 2 }}>
						<AlertTitle>Error al procesar el archivo</AlertTitle>
						<Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
							{parseError}
						</Typography>
					</Alert>
				)}
			</Box>
		);
	}

	return (
		<Box>
			{step === 'done' && submitResult?.error ? (
				<Alert severity="error" sx={{ mb: 3 }}>
					<AlertTitle>Error en la importación</AlertTitle>
					{submitResult.error}
				</Alert>
			) : null}

			{step === 'done' && !submitResult?.error ? (
				<Alert severity={submitResult?.failed ? 'warning' : 'success'} sx={{ mb: 3 }}>
					<AlertTitle>
						{submitResult?.failed ? 'Importación finalizada con observaciones' : 'Importación completada'}
					</AlertTitle>
					Se crearon correctamente <strong>{submitResult?.success ?? 0}</strong> pago
					{submitResult?.success !== 1 ? 's' : ''}
					{(submitResult?.failed ?? 0) > 0 ? (
						<>
							{' '}
							y fallaron <strong>{submitResult?.failed}</strong>.
						</>
					) : (
						'.'
					)}
				</Alert>
			) : null}

			<Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
				<Chip label={fileName} variant="outlined" size="small" />
				<Chip label={`${totalRows} fila${totalRows !== 1 ? 's' : ''}`} size="small" />
				<Chip label={`${groups.length} línea${groups.length !== 1 ? 's' : ''}`} size="small" />
				<Chip
					icon={<CheckCircleOutlineIcon />}
					label={`${validGroups.length} válida${validGroups.length !== 1 ? 's' : ''}`}
					color="success"
					size="small"
				/>
				{invalidGroups.length > 0 && (
					<Chip
						icon={<ErrorOutlineIcon />}
						label={`${invalidGroups.length} con error${invalidGroups.length !== 1 ? 'es' : ''}`}
						color="error"
						size="small"
					/>
				)}
			</Stack>

			{step === 'preview' && invalidGroups.length > 0 && (
				<Alert severity="warning" sx={{ mb: 2 }}>
					Hay líneas con errores. Corregí el CSV y volvé a subir para poder importar todas.
				</Alert>
			)}

			<TableContainer component={Paper} variant="outlined" sx={{ mb: 3, maxHeight: 540 }}>
				<Table size="small" stickyHeader>
					<TableHead>
						<TableRow>
							<TableCell sx={{ fontWeight: 700 }}>ID Línea</TableCell>
							<TableCell sx={{ fontWeight: 700 }}>Fecha</TableCell>
							<TableCell sx={{ fontWeight: 700 }} align="right">
								Monto (USD)
							</TableCell>
							<TableCell sx={{ fontWeight: 700 }}>Tipo</TableCell>
							<TableCell sx={{ fontWeight: 700 }}>Nota</TableCell>
							<TableCell sx={{ fontWeight: 700 }}>Observación</TableCell>
							<TableCell sx={{ fontWeight: 700 }}>Moneda</TableCell>
							<TableCell sx={{ fontWeight: 700 }} align="right">
								TC
							</TableCell>
							<TableCell sx={{ fontWeight: 700 }} align="right">
								Pendiente línea
							</TableCell>
							<TableCell sx={{ fontWeight: 700 }}>Estado</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{groups.map((group) => (
							<React.Fragment key={group.providerPayableLineId}>
								<TableRow
									sx={{
										bgcolor: group.isValid ? 'rgba(46, 125, 50, 0.08)' : 'rgba(211, 47, 47, 0.08)',
									}}
								>
									<TableCell colSpan={8}>
										<Stack direction="row" spacing={1} alignItems="center">
											{group.isValid ? (
												<CheckCircleOutlineIcon fontSize="small" color="success" />
											) : (
												<ErrorOutlineIcon fontSize="small" color="error" />
											)}
											<Typography variant="body2" sx={{ fontWeight: 700, fontFamily: 'monospace' }}>
												{group.providerPayableLineId}
											</Typography>
											<Typography variant="caption" color="text.secondary">
												· {group.rows.length} pago{group.rows.length !== 1 ? 's' : ''} · suma:{' '}
												<strong>{fmtUSD(group.totalAmount)}</strong>
											</Typography>
											{group.groupErrors.map((err, i) => (
												<Typography key={i} variant="caption" color="error.main">
													· {err}
												</Typography>
											))}
										</Stack>
									</TableCell>
									<TableCell align="right">
										<Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
											{group.pending !== undefined ? fmtUSD(group.pending) : '—'}
										</Typography>
									</TableCell>
									<TableCell />
								</TableRow>

								{group.rows.map((row) => {
									const rowErrs = group.rowErrors[row.rowIndex];
									const rowImportResult = group.rowImportResults[row.rowIndex];

									return (
										<TableRow
											key={row.rowIndex}
											sx={{
												bgcolor:
													rowErrs || rowImportResult?.status === 'Failed'
														? 'rgba(211, 47, 47, 0.04)'
														: rowImportResult?.status === 'Created'
														? 'rgba(46, 125, 50, 0.04)'
														: 'inherit',
											}}
										>
											<TableCell sx={{ color: 'text.disabled', fontSize: '0.72rem', pl: 4 }}>#{row.rowIndex}</TableCell>
											<TableCell>{row.paymentDate || '—'}</TableCell>
											<TableCell
												align="right"
												sx={{
													fontWeight: 600,
													color: isNaN(row.amount) ? 'text.disabled' : row.amount < 0 ? 'error.main' : 'text.primary',
												}}
											>
												{isNaN(row.amount) ? '—' : fmtUSD(row.amount)}
											</TableCell>
											<TableCell>{row.paymentType || '—'}</TableCell>
											<TableCell
												sx={{
													maxWidth: 140,
													overflow: 'hidden',
													textOverflow: 'ellipsis',
													whiteSpace: 'nowrap',
												}}
											>
												<Tooltip title={row.notes} placement="top" disableHoverListener={!row.notes}>
													<span>{row.notes || '—'}</span>
												</Tooltip>
											</TableCell>
											<TableCell
												sx={{
													maxWidth: 140,
													overflow: 'hidden',
													textOverflow: 'ellipsis',
													whiteSpace: 'nowrap',
												}}
											>
												<Tooltip title={row.observation} placement="top" disableHoverListener={!row.observation}>
													<span>{row.observation || '—'}</span>
												</Tooltip>
											</TableCell>
											<TableCell>{row.exchangeRate !== undefined ? row.localCurrency || '—' : '—'}</TableCell>
											<TableCell align="right">{row.exchangeRate !== undefined ? row.exchangeRate : '—'}</TableCell>
											<TableCell />
											<TableCell>
												{rowErrs ? (
													<Tooltip title={rowErrs.join(' · ')} placement="left">
														<Chip label="Error" color="error" size="small" />
													</Tooltip>
												) : step === 'done' && rowImportResult?.status === 'Failed' ? (
													<Tooltip title={rowImportResult.error || 'Error al crear el pago'} placement="left">
														<Chip label="Error" color="error" size="small" />
													</Tooltip>
												) : step === 'done' && rowImportResult?.status === 'Created' ? (
													<Tooltip
														title={
															rowImportResult.paymentId
																? `ID pago: ${rowImportResult.paymentId}`
																: 'Creado correctamente'
														}
														placement="left"
													>
														<Chip label="Creado" color="success" size="small" variant="outlined" />
													</Tooltip>
												) : (
													<Chip label="OK" color="success" size="small" variant="outlined" />
												)}
											</TableCell>
										</TableRow>
									);
								})}
							</React.Fragment>
						))}
					</TableBody>
				</Table>
			</TableContainer>

			<Stack direction="row" spacing={2}>
				<Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={handleReset}>
					{step === 'done' ? 'Importar otro archivo' : 'Volver a subir'}
				</Button>

				{step === 'preview' && (
					<Button
						variant="contained"
						color="primary"
						startIcon={<SendIcon />}
						onClick={handleSubmit}
						disabled={invalidGroups.length > 0}
					>
						Confirmar e importar ({validGroups.length} línea{validGroups.length !== 1 ? 's' : ''})
					</Button>
				)}
			</Stack>
		</Box>
	);
};
